import { inject, Injectable, NgZone, SecurityContext } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { BehaviorSubject, filter, Subscription, take } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';

import { TourPopoverComponent } from './tour-popover.component';
import { Dialog } from '../common/dialog.service';
import { TourExitConfirmDialogComponent } from './tour-exit-confirm-dialog.component';
import type { ActiveStepView, TourPlacement, TourStepViewModel, TourViewModel } from './tour.types';
import { TranslationService, UserService, UserTourService } from '../common';
import { AppState } from '../common/app-state';

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly router = inject(Router);
  private readonly overlay = inject(Overlay);
  private readonly zone = inject(NgZone);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly userService = inject(UserService);
  private readonly tourService = inject(UserTourService);
  private readonly translationService = inject(TranslationService);
  private readonly dialog = inject(Dialog);
  private running = false;

  private currentTour?: TourViewModel;
  private idx = -1;
  private startTourRoute: string | null = null;

  private overlayRef?: OverlayRef;
  private anchorEl: HTMLElement | null = null;
  private currentPadding = 6;
  private navSub?: Subscription;
  private posSub?: Subscription;
  private pendingNavAdvanceIdx: number | null = null;

  readonly active$ = new BehaviorSubject<ActiveStepView | null>(null);
  private readonly appState = inject(AppState);
  private readonly tourIdQueryParam = 'tourId';
  private readonly tourStepQueryParam = 'tourStep';
  private pendingAutostartTourIds = new Set<string>();
  private autostartLoaded = false;

  constructor() {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(async () => {
      if (!this.appState.isLoggedIn$.value) {
        return;
      }
      if (!this.currentTour && !this.running) {
        this.running = true;
        try {
          await this.resumeTourFromUrl();
        } finally {
          this.running = false;
        }
      }
    });
  }

  private updateSpotlight = () => {
    const pad = this.currentPadding;
    const rect = this.anchorEl?.getBoundingClientRect();
    if (!rect) {
      document.documentElement.style.setProperty('--tour-spot-x', '-9999px');
      document.documentElement.style.setProperty('--tour-spot-y', '-9999px');
      document.documentElement.style.setProperty('--tour-spot-w', '1px');
      document.documentElement.style.setProperty('--tour-spot-h', '1px');
      return;
    }
    document.documentElement.style.setProperty('--tour-spot-x', `${Math.max(rect.left - pad, 0)}px`);
    document.documentElement.style.setProperty('--tour-spot-y', `${Math.max(rect.top - pad, 0)}px`);
    document.documentElement.style.setProperty('--tour-spot-w', `${rect.width + pad * 2}px`);
    document.documentElement.style.setProperty('--tour-spot-h', `${rect.height + pad * 2}px`);
  };

  private popHandler = () => {
    // Cancel Back/Forward while a tour is active
    history.pushState(null, '', window.location.href);
  };

  private isMobile(): boolean {
    return window.matchMedia('(max-width: 1023px)').matches;
  }
  async startPendingTour() {
    if (this.running) return;
    this.running = true;
    try {
      if (await this.resumeTourFromUrl()) {
        return;
      }
      await this.ensureAutostartLoaded();
    } finally {
      this.running = false;
    }
  }

  async maybeAutostartTourById(tourId: string): Promise<void> {
    const id = (tourId || '').trim();
    if (!id || this.currentTour || this.running) {
      return;
    }
    this.running = true;
    try {
      await this.ensureAutostartLoaded();
      if (!this.pendingAutostartTourIds.has(id)) {
        return;
      }
      const detail = await this.tourService.localizedDetail(id, this.translationService.language);
      if (!detail?.steps?.length || detail.completed || detail.lastAbandonedAt) {
        this.pendingAutostartTourIds.delete(id);
        return;
      }
      this.pendingAutostartTourIds.delete(id);
      await this.startTour(id);
    } finally {
      this.running = false;
    }
  }

  async startTour(
    tourName: string,
    options: {
      stepIndex?: number;
      resume?: boolean;
    } = {}
  ) {
    const { stepIndex = 0, resume = false } = options;
    const tour = await this.tourService.localizedDetail(tourName, this.translationService.language);
    if (!tour || !tour.steps.length) {
      return;
    }
    if (!resume) {
      this.startTourRoute = this.router.url;
    }
    const steps = tour.steps.map((step, i) => ({
      ...step,
      step: i + 1,
      element: step.cssSelector || 'body',
      within: undefined,
      anchorClosest: step.cssParentSelector ?? step.anchorClosest,
      placement: (step.cssSelector ? step.placement : 'center') ?? 'auto',
      padding: 6,
      beforeDelayMs: 150,
    }));

    this.currentTour = { id: tour.id, steps: [...steps] };
    this.idx = Math.min(Math.max(stepIndex, 0), steps.length - 1);

    // Record tour start unless resuming from URL
    if (!resume) {
      await this.userService.startTour(tour.id);
    }

    if (tour.startRoute && this.router.url !== tour.startRoute) {
      await this.router.navigateByUrl(tour.startRoute);
      await this.waitForNavEndOrTimeout(300);
    }

    // One subscription for SPA navigations
    this.navSub?.unsubscribe();
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(async () => {
        await this.whenStable();
        if (!this.currentTour) return;

        const urlState = this.getTourStateFromUrl();
        if (urlState && urlState.id === this.currentTour.id && urlState.step !== this.idx) {
          this.idx = Math.min(Math.max(urlState.step, 0), this.currentTour.steps.length - 1);
          await this.goto(this.idx);
          return;
        }

        if (this.pendingNavAdvanceIdx != null) {
          const nextIdx = Math.min(this.pendingNavAdvanceIdx, this.currentTour.steps.length - 1);
          this.pendingNavAdvanceIdx = null;
          this.idx = nextIdx;
          await this.goto(this.idx);
          return;
        }

        const step = this.currentTour.steps[this.idx];
        if (step?.autoAdvanceOnNav) {
          await this.next();
          return;
        }

        await this.tryRenderCurrentStep();
      });

    // Lock back, keep spotlight fresh on resize/scroll
    history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', this.popHandler);
    window.addEventListener('resize', this.updateSpotlight);
    window.addEventListener('scroll', this.updateSpotlight, true);
    await this.goto(this.idx);
  }

  get isActive(): boolean {
    return !!this.currentTour;
  }

  isActiveStepRoute(targetUrl: string): boolean {
    const stepRoute = this.currentTour?.steps[this.idx]?.route;
    if (!stepRoute) {
      return false;
    }
    const pathOnly = (url: string) => url.split(/[?#]/, 1)[0].replace(/\/$/, '') || '/';
    return pathOnly(targetUrl) === pathOnly(stepRoute);
  }

  async next() {
    if (!this.currentTour) return;
    if (this.idx < this.currentTour.steps.length - 1) {
      this.idx++;
      await this.goto(this.idx);
    } else {
      await this.finish(true, true);
    }
  }

  async prev() {
    if (this.idx > 0) {
      this.idx--;
      await this.goto(this.idx);
    }
  }

  async finish(remeberState: boolean, lastStep: boolean) {
    if (this.currentTour && lastStep) {
      // User finished the tour (reached last step)
      await this.userService.finishTour(this.currentTour.id);
    } else if (this.currentTour && remeberState) {
      // User abandoned the tour (exited early)
      await this.userService.abandonTour(this.currentTour.id);
    }
    this.clearTourUrlState();
    this.detach();
    this.clearAnchorHighlight();
    this.anchorEl = null;
    this.active$.next(null);
    this.navSub?.unsubscribe();
    this.navSub = undefined;
    this.pendingNavAdvanceIdx = null;
    window.removeEventListener('popstate', this.popHandler);
    window.removeEventListener('resize', this.updateSpotlight);
    window.removeEventListener('scroll', this.updateSpotlight, true);

    this.unbindAnchorClick();
    this.currentTour = undefined;
    this.idx = -1;
    const returnRoute = this.startTourRoute;
    this.startTourRoute = null;

    if (returnRoute && this.router.url !== returnRoute) {
      await this.router.navigateByUrl(returnRoute);
    }

  }

  private async goto(index: number) {
    const step = this.currentTour?.steps[index];
    if (!step) return;

    await this.userService.updateTourProgress(this.currentTour!.id, index);
    const targetRoute = step.route;
    const requiresRouteChange = !!targetRoute && this.router.url !== targetRoute;

    if (requiresRouteChange) {
      // Prevent CDK from pinning the old step to (0,0) while the previous anchor is destroyed.
      this.detach();
      this.clearAnchorHighlight();
      this.anchorEl = null;
    }

    if (step.openSideNav) {
      this.appState.openSideNav$.next(true);
      await this.waitForNavEndOrTimeout(300);
    } else {
      if (this.isMobile()) {
        this.appState.openSideNav$.next(false);
      }
    }

    if (targetRoute && requiresRouteChange) {
      await this.router.navigateByUrl(targetRoute);
      await this.waitForRouteAndStable(targetRoute, 2500);
    }

    if (step.beforeDelayMs) await this.delay(step.beforeDelayMs);

    try {
      const attached = await this.renderStep(step);
      if (!attached) {
        this.handleStepFailure();
      }
    } catch (error) {
      console.error('Tour step failed to render', error);
      this.handleStepFailure();
    }
  }

  private async tryRenderCurrentStep() {
    if (!this.currentTour || this.idx < 0) return;
    const step = this.currentTour.steps[this.idx];
    if (!step) return;

    try {
      const attached = await this.renderStep(step);
      if (!attached) {
        this.handleStepFailure();
      }
    } catch (error) {
      console.error('Tour step failed to render', error);
      this.handleStepFailure();
    }
  }

  private handleStepFailure() {
    this.active$.next(null);
    this.detach();
    this.clearAnchorHighlight();
    this.anchorEl = null;
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.clearTourUrlState();
  }

  private updateTourUrlState(tourId?: string, stepIndex?: number) {
    const url = new URL(window.location.href);
    if (tourId != null) {
      url.searchParams.set(this.tourIdQueryParam, tourId);
      const step = stepIndex != null ? stepIndex + 1 : 1;
      url.searchParams.set(this.tourStepQueryParam, String(step));
    } else {
      url.searchParams.delete(this.tourIdQueryParam);
      url.searchParams.delete(this.tourStepQueryParam);
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  private clearTourUrlState() {
    this.updateTourUrlState();
  }

  private getTourStateFromUrl(): { id: string; step: number } | null {
    const url = new URL(window.location.href);
    const id = url.searchParams.get(this.tourIdQueryParam);
    if (!id) {
      return null;
    }
    const stepParam = url.searchParams.get(this.tourStepQueryParam);
    const parsed = stepParam ? parseInt(stepParam, 10) : NaN;
    const step = !isNaN(parsed) && parsed > 0 ? parsed - 1 : 0;
    return { id, step };
  }

  private async resumeTourFromUrl(): Promise<boolean> {
    const state = this.getTourStateFromUrl();
    if (!state) {
      return false;
    }
    await this.startTour(state.id, { stepIndex: state.step, resume: true });
    return true;
  }

  private async ensureAutostartLoaded(): Promise<void> {
    if (this.autostartLoaded) {
      return;
    }
    const profile = await this.userService.profile();
    const ids = profile.autostartTours ?? [];
    this.pendingAutostartTourIds = new Set(ids.filter((id): id is string => !!id));
    this.autostartLoaded = true;
  }

  private async renderStep(step: TourStepViewModel): Promise<boolean> {
    const prevAnchor = this.anchorEl;
    this.anchorEl = await this.resolveTarget(step, 3000, 50);

    const isCenter = (step.placement ?? 'auto') === 'center' || (!step.element && !step.text);
    if (isCenter) this.anchorEl = null;

    // Update highlight target if it changed
    if (prevAnchor && prevAnchor !== this.anchorEl) prevAnchor.classList.remove('tour-highlight');

    this.anchorEl?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    await this.delay(step.scrollMargin ? 250 : 50);

    this.rebindAnchorClick(true);
    const popoverAttached = this.attachPopover(step);
    if (!popoverAttached) {
      return false;
    }

    // Button visibility from hide[]
    const hideSet = new Set((step.hide ?? []).map((h) => (h ?? '').toLowerCase()));

    this.active$.next({
      idx: this.idx,
      total: this.currentTour!.steps.length,
      title: this.sanitizeTourHtml(step.title),
      message: this.sanitizeTourHtml(step.message),
      placement: step.placement ?? 'auto',
      hasPrev: this.idx > 0,
      hasNext: this.idx < this.currentTour!.steps.length - 1,
      showPrev: !hideSet.has('previous') && this.idx > 0,
      showNext: !hideSet.has('next') && this.idx < this.currentTour!.steps.length - 1,
      showFinish: this.idx === this.currentTour!.steps.length - 1,
      showClose: !hideSet.has('close'),
      showBeacon: !hideSet.has('beacon'),
      accentColor: step.accentColor,
    });
    this.updateTourUrlState(this.currentTour!.id, this.idx);

    return true;
  }

  private attachPopover(step: TourStepViewModel): boolean {
    if (!this.anchorEl && (step.placement ?? 'auto') !== 'center') {
      if (this.overlayRef?.hasAttached()) this.overlayRef.detach();
      document.body.classList.remove('tour-lockout');
      return false;
    }

    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        hasBackdrop: true,
        backdropClass: 'tour-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        disposeOnNavigation: false,
        panelClass: 'tour-pane',
        positionStrategy: this.overlay.position().global(),
        usePopover: false,
      });
    } else {
      this.overlayRef.addPanelClass('tour-pane');
    }

    const requested = step.placement ?? 'auto';
    const positions: ConnectedPosition[] = [
      { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
      { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
      { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
      { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
    ];

    let strategy: FlexibleConnectedPositionStrategy | GlobalPositionStrategy;

    if (this.anchorEl && requested !== 'center') {
      const flex = this.overlay
        .position()
        .flexibleConnectedTo(this.anchorEl)
        .withFlexibleDimensions(false)
        .withPush(true)
        .withViewportMargin(16)
        .withPositions(this.orderPositions(requested, positions));
      strategy = flex;

      this.posSub?.unsubscribe();
      this.posSub = flex.positionChanges.subscribe(({ connectionPair }) => {
        const p = this.mapPairToPlacement(connectionPair);
        const vm = this.active$.value;
        if (vm && vm.placement !== p) this.active$.next({ ...vm, placement: p });
        this.updateArrowAndBeaconOffsets(p);
        this.updateSpotlight();
      });
    } else {
      strategy = this.overlay.position().global().centerHorizontally().centerVertically();
      this.posSub?.unsubscribe();
      this.pushResolvedPlacement('center');
      this.updateArrowAndBeaconOffsets('center');
    }

    this.overlayRef.updatePositionStrategy(strategy);

    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(new ComponentPortal(TourPopoverComponent));
    } else {
      this.overlayRef.updatePosition();
    }
    this.bringOverlayToFront();

    // First layout pass => sync placement/arrow/spotlight
    queueMicrotask(() => {
      if (this.anchorEl && this.overlayRef) {
        const p = this.inferPlacementByRects(this.anchorEl, this.overlayRef.overlayElement);
        this.pushResolvedPlacement(p);
        this.updateArrowAndBeaconOffsets(p);
        this.updateSpotlight();
      }
    });

    // Backdrop style (spotlight vs solid)
    const backdrop = this.overlayRef.backdropElement;
    if (backdrop) {
      backdrop.classList.add('tour-backdrop');
    }

    // Anchor highlight + lock body interactions
    this.anchorEl?.classList.add('tour-highlight');
    document.body.classList.add('tour-lockout');

    this.currentPadding = step.padding ?? 6;
    this.updateSpotlight();
    return true;
  }

  private orderPositions(pref: TourPlacement, base: ConnectedPosition[]) {
    if (pref === 'auto' || pref === 'center') return base;
    const map: Record<'bottom' | 'top' | 'right' | 'left', number> = { bottom: 0, top: 1, right: 2, left: 3 };
    const preferred = base[map[pref as keyof typeof map]];
    return [preferred, ...base.filter((p) => p !== preferred)];
  }

  private detach() {
    this.posSub?.unsubscribe();
    this.posSub = undefined;
    if (this.overlayRef?.hasAttached()) this.overlayRef.detach();
  }

  private clearAnchorHighlight() {
    this.anchorEl?.classList.remove('tour-highlight');
    document.body.classList.remove('tour-lockout');
  }

  /* ---------------- Click-to-advance ---------------- */

  private unbindAnchorClick() {
    if (this.anchorEl && (this.anchorEl as any).__tourClickHandler) {
      this.anchorEl.removeEventListener('click', (this.anchorEl as any).__tourClickHandler);
      delete (this.anchorEl as any).__tourClickHandler;
    }
  }

  private rebindAnchorClick(enable: boolean) {
    this.unbindAnchorClick();
    if (!enable || !this.anchorEl) return;

    const handler = async (event?: Event) => {
      event?.preventDefault();
      event?.stopPropagation();
      // tell nav listener to advance; but KEEP the overlay attached
      this.pendingNavAdvanceIdx = this.idx + 1;

      // Soft-hide to avoid “blink”
      this.overlayRef?.overlayElement.classList.add('tour-fade');
      await this.waitForNavEndOrTimeout(1200);

      this.overlayRef?.overlayElement.classList.remove('tour-fade');

      if (this.pendingNavAdvanceIdx != null && this.currentTour) {
        const nextIdx = Math.min(this.pendingNavAdvanceIdx, this.currentTour.steps.length - 1);
        const nextStep = this.currentTour.steps[nextIdx];
        if (nextStep?.route && this.router.url !== nextStep.route) {
          // Route transition still in progress; let the NavigationEnd subscription advance.
          return;
        }
        this.pendingNavAdvanceIdx = null;
        this.idx = nextIdx;
        await this.whenStable();
        await this.goto(this.idx);
      }
    };

    (this.anchorEl as any).__tourClickHandler = handler;
    this.anchorEl.addEventListener('click', handler, { once: true, capture: true });
  }

  private async resolveTarget(step: TourStepViewModel, timeoutMs = 3000, intervalMs = 50): Promise<HTMLElement | null> {
    const deadline = performance.now() + timeoutMs;

    const rawElement = step.element ? this.stripOuterQuotes(step.element) : undefined;
    const dsl = rawElement ? this.parseElementDsl(rawElement) : null;

    if (step.element === 'body') return document.body as HTMLElement;

    const params = {
      within: step.within ?? dsl?.within,
      css: (rawElement && !dsl?.isPureText ? (dsl?.css ?? rawElement) : dsl?.css) ?? '*',
      text: step.text ?? dsl?.text,
      mode: step.textMatch ?? dsl?.mode ?? 'contains',
      ci: step.caseInsensitive ?? dsl?.ci ?? true,
      nth: dsl?.nth ?? 1, // keep DSL support; TourStep doesn’t define nth
    } as const;

    const exclude = ['.circle-number', '.mat-badge', '[aria-hidden="true"]', '.visually-hidden'];

    while (performance.now() < deadline) {
      if (params.text) {
        const raw = this.findByText(
          params.within,
          params.css,
          params.text,
          params.mode as any,
          params.ci,
          params.nth,
          exclude
        );
        const promoted = raw ? this.promoteAnchor(raw, step.anchorClosest) : null;
        if (promoted) return promoted;
      }

      if (!params.text && rawElement && !dsl) {
        const plain = this.findByText(params.within, '*', rawElement, 'contains', true, 1, exclude);
        const promoted = plain ? this.promoteAnchor(plain, step.anchorClosest) : null;
        if (promoted) return promoted;
      }

      if (!params.text && rawElement && !dsl && this.looksLikeCssSelector(rawElement)) {
        try {
          const el = document.querySelector(rawElement) as HTMLElement | null;
          if (el && this.isVisible(el)) {
            const promoted = this.promoteAnchor(el, step.anchorClosest);
            if (promoted && this.isVisible(promoted)) return promoted;
          }
        } catch {
          /* ignore invalid selector */
        }
      }

      if (!params.text && rawElement && !dsl && this.looksLikeCssSelector(rawElement)) {
        try {
          const scope =
            (params.within ? this.firstVisible(params.within) : document.documentElement) ?? document.documentElement;

          const candidates = Array.from(scope.querySelectorAll<HTMLElement>(rawElement));
          for (const el of candidates) {
            if (!this.isVisible(el)) continue;
            const promoted = this.promoteAnchor(el, step.anchorClosest);
            if (promoted && this.isVisible(promoted)) return promoted;
          }
        } catch {
          /* ignore invalid selector */
        }
      }

      await this.delay(intervalMs);
    }
    return null;
  }

  private findByText(
    withinSelector: string | undefined,
    cssSelector: string,
    text: string,
    mode: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'regex',
    ci: boolean,
    nth: number,
    excludeSelectors: string[] = []
  ): HTMLElement | null {
    const scope = withinSelector ? this.firstVisible(withinSelector) : document.documentElement;
    if (!scope) return null;

    const list = Array.from(scope.querySelectorAll<HTMLElement>(cssSelector || '*'));
    let count = 0;

    const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
    const cmp = (s: string) => (ci ? s.toLowerCase() : s);
    const needle = cmp(text);

    let regex: RegExp | null = null;
    if (mode === 'regex') {
      const m = /^\/(.+)\/([gimsuy]*)$/.exec(text);
      try {
        regex = m ? new RegExp(m[1], m[2]) : new RegExp(text, ci ? 'i' : '');
      } catch {
        regex = null;
      }
    }

    for (const el of list) {
      let raw = el.innerText || el.textContent || '';
      for (const sel of excludeSelectors) {
        el.querySelectorAll<HTMLElement>(sel).forEach((child) => {
          const t = child.innerText || child.textContent || '';
          if (t) raw = raw.replace(t, '');
        });
      }

      const t = norm(raw);
      const c = cmp(t);

      let ok = false;
      switch (mode) {
        case 'equals':
          ok = c === needle;
          break;
        case 'startsWith':
          ok = c.startsWith(needle);
          break;
        case 'endsWith':
          ok = c.endsWith(needle);
          break;
        case 'regex':
          ok = !!regex && regex.test(t);
          break;
        default:
          ok = c.includes(needle);
      }

      if (ok && this.isVisible(el)) {
        count++;
        if (count === (nth || 1)) return el;
      }
    }
    return null;
  }

  private promoteAnchor(el: HTMLElement, customClosest?: string): HTMLElement {
    const CANDIDATES = (
      customClosest
        ?.split(',')
        .map((s) => s.trim())
        .filter(Boolean) ?? []
    ).concat([
      '.mat-mdc-list-item',
      'mat-list-item',
      '.mdc-list-item',
      'a[routerlink]',
      '[routerLink]',
      'a[href]',
      'button',
      '[role="button"]',
      'li',
    ]);

    for (const sel of CANDIDATES) {
      const hit = el.closest(sel) as HTMLElement | null;
      if (hit && this.isVisible(hit)) return hit;
    }

    let node: HTMLElement = el;
    let steps = 0;
    while (node.parentElement && steps < 6) {
      const style = getComputedStyle(node);
      const blocky = ['block', 'flex', 'grid', 'list-item', 'inline-block'].includes(style.display);
      const areaOk = node.offsetWidth * node.offsetHeight > 20;
      if (blocky && areaOk && this.isVisible(node)) return node;
      node = node.parentElement!;
      steps++;
    }
    return el;
  }

  private isVisible(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    const style = getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }

  private firstVisible(selectorCSV: string): HTMLElement | null {
    for (const sel of selectorCSV
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)) {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el && this.isVisible(el)) return el;
    }
    return null;
  }

  private updateArrowAndBeaconOffsets(p: TourPlacement) {
    if (!this.overlayRef || !this.anchorEl) return;
    const panel = this.overlayRef.overlayElement;
    const o = panel.getBoundingClientRect();
    const a = this.anchorEl.getBoundingClientRect();

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
    const AR = 10; // arrow half-size
    const B = 12; // beacon half-size
    const PAD = 8;

    if (p === 'left' || p === 'right') {
      const y = clamp(a.top + a.height / 2 - o.top - AR, PAD, o.height - 2 * AR - PAD);
      panel.style.setProperty('--arrow-offset-y', `${Math.round(y)}px`);
      panel.style.setProperty('--beacon-offset-y', `${Math.round(y + AR - B)}px`);
      panel.style.removeProperty('--arrow-offset-x');
      panel.style.removeProperty('--beacon-offset-x');
    } else if (p === 'top' || p === 'bottom') {
      const x = clamp(a.left + a.width / 2 - o.left - AR, PAD, o.width - 2 * AR - PAD);
      panel.style.setProperty('--arrow-offset-x', `${Math.round(x)}px`);
      panel.style.setProperty('--beacon-offset-x', `${Math.round(x + AR - B)}px`);
      panel.style.removeProperty('--arrow-offset-y');
      panel.style.removeProperty('--beacon-offset-y');
    } else {
      panel.style.removeProperty('--arrow-offset-x');
      panel.style.removeProperty('--arrow-offset-y');
      panel.style.removeProperty('--beacon-offset-x');
      panel.style.removeProperty('--beacon-offset-y');
    }
  }

  /* ---------------- Small parsing helpers ---------------- */

  // Add inside TourService class

  /** Infer actual placement from rendered panel vs anchor (after CDK push/flip). */
  private inferPlacementByRects(anchor: HTMLElement, overlayEl: HTMLElement): TourPlacement {
    const a = anchor.getBoundingClientRect();
    const o = overlayEl.getBoundingClientRect();
    const tol = 2; // tolerance for sub-pixel

    const isBottom = o.top >= a.bottom - tol;
    const isTop = o.bottom <= a.top + tol;
    const isRight = o.left >= a.right - tol;
    const isLeft = o.right <= a.left + tol;

    if (isBottom) return 'bottom';
    if (isTop) return 'top';
    if (isRight) return 'right';
    if (isLeft) return 'left';
    return 'auto';
  }

  /** Push the resolved placement into the active VM so arrow/beacon CSS can react. */
  private pushResolvedPlacement(p: TourPlacement) {
    const vm = this.active$.value;
    if (!vm) return;
    if (vm.placement !== p) {
      this.active$.next({ ...vm, placement: p });
    }
  }

  private parseElementDsl(input: string): {
    within?: string;
    css?: string;
    text?: string;
    mode?: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'regex';
    ci?: boolean;
    nth?: number;
    isPureText?: boolean;
  } | null {
    const s = this.stripOuterQuotes(input);
    const chain = /^css=(.+?)\s*>>\s*text=(.+)$/i.exec(s);
    if (chain) {
      const css = chain[1].trim();
      const textSpec = chain[2].trim();
      const parsedText = this.parseTextSpec(textSpec);
      return { css, ...parsedText, isPureText: false };
    }
    if (/^text=/i.test(s)) {
      const parsedText = this.parseTextSpec(s.slice(5).trim());
      return { css: '*', ...parsedText, isPureText: true };
    }
    return null;
  }

  private parseTextSpec(spec: string): {
    text?: string;
    mode?: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'regex';
    ci?: boolean;
    nth?: number;
  } {
    const q = /^([*^$])?\s*(?:"([^"]+)"|'([^']+)')\s*$/.exec(spec);
    if (q) {
      const op = q[1];
      const val = (q[2] ?? q[3] ?? '').trim();
      const mode = op === '*' ? 'contains' : op === '^' ? 'startsWith' : op === '$' ? 'endsWith' : 'equals';
      return { text: val, mode, ci: true };
    }
    const r = /^\/(.+)\/([gimsuy]*)$/.exec(spec);
    if (r) return { text: spec, mode: 'regex', ci: false };
    return { text: spec.replace(/^["']|["']$/g, ''), mode: 'equals', ci: true };
  }

  private stripOuterQuotes(input: string): string {
    let s = (input ?? '').trim();
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) s = s.slice(1, -1).trim();
    return s;
  }

  private looksLikeCssSelector(s: string): boolean {
    return /[#.[\]:]/.test(s) || /\s[>+~]\s?/.test(s) || (/\s/.test(s) && /[.#[]/.test(s));
  }

  private bringOverlayToFront() {
    if (!this.overlayRef) {
      return;
    }
    const host = this.overlayRef.hostElement;
    const pane = this.overlayRef.overlayElement;
    const container = host.parentElement;
    const z = 3000;
    host.style.zIndex = String(z);
    pane.style.zIndex = String(z);
    if (container && container.lastElementChild !== host) {
      container.appendChild(host);
    }
    const backdrop = this.overlayRef.backdropElement;
    if (backdrop) {
      const backdropParent = backdrop.parentElement;
      if (backdropParent && backdropParent.lastElementChild !== backdrop) {
        backdropParent.appendChild(backdrop);
      }
    }
  }

  showExitDialog = false;

  async requestExitViaCloseButton() {
    try {
      if (this.showExitDialog) return;
      this.showExitDialog = true;

      const choice = await this.dialog.open(TourExitConfirmDialogComponent, { maxWidth: '420px' });
      if (choice !== undefined) await this.finish(choice, false);
    } finally {
      this.showExitDialog = false;
    }
  }

  private delay(ms: number) {
    return new Promise<void>((res) => setTimeout(res, ms));
  }

  private waitForNavEndOrTimeout(timeoutMs = 300): Promise<void> {
    return new Promise((resolve) => {
      let resolved = false;
      const sub = this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          take(1)
        )
        .subscribe(() => {
          if (!resolved) {
            resolved = true;
            resolve();
          }
        });
      setTimeout(() => {
        if (!resolved) {
          sub.unsubscribe();
          resolve();
        }
      }, timeoutMs);
    });
  }

  private waitForRouteAndStable(route: string, timeoutMs = 2500): Promise<void> {
    if (this.router.url === route) {
      return this.whenStable();
    }
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        sub.unsubscribe();
        clearTimeout(timer);
        void this.whenStable().then(resolve);
      };
      const sub = this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => {
          if (this.router.url === route) {
            finish();
          }
        });
      const timer = setTimeout(() => finish(), timeoutMs);
    });
  }

  private whenStable(): Promise<void> {
    return new Promise<void>((resolve) => {
      const sub = this.zone.onStable.pipe(take(1)).subscribe(() => {
        requestAnimationFrame(() => resolve());
        sub.unsubscribe();
      });
    });
  }

  private sanitizeTourHtml(value: string | null | undefined): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, value ?? '') ?? '';
  }

  private mapPairToPlacement(pair: ConnectedPosition): TourPlacement {
    if (pair.overlayY === 'top' && pair.originY === 'bottom') return 'bottom';
    if (pair.overlayY === 'bottom' && pair.originY === 'top') return 'top';
    if (pair.overlayX === 'start' && pair.originX === 'end') return 'right';
    if (pair.overlayX === 'end' && pair.originX === 'start') return 'left';
    return 'auto';
  }
}
